/* eslint-disable no-restricted-globals */
import React, { useState, useEffect, useRef, useCallback } from "react";
import styled from "styled-components";
import { Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";

// components
import ImagePicker from "./ImagePicker";
import { TextArea, TextInput } from "components/form";
import { Button } from "components/common";

// utils
import { api } from "utils";
import { compareObjects } from "utils/object";

// redux
import { updatePartnerInfo } from "redux/slices/partner/partnerSlice";

// hooks
import { usePrompt } from "hooks/usePrompt";

const ImageSettingsEditForm = ({ info, setLoading }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const pageRef = useRef();
  const [height, setHeight] = useState(window.innerHeight);
  const [deletedImages, setDeletedImages] = useState([]);
  const [formikValues, setFormikValues] = useState({});
  const [dirty, setDirty] = useState(false);
  const [initialValues, setInitialValues] = useState({
    profileImage: info?.profileImage
      ? [{ id: 1, url: info?.profileImage }]
      : [],
    memo: info?.memo || "",
    tag1: info?.hashTags ? info?.hashTags[0]?.hashTag : "",
    tag2: info?.hashTags ? info?.hashTags[1]?.hashTag : "",
    tag3: info?.hashTags ? info?.hashTags[2]?.hashTag : "",
  });

  useEffect(() => {
    window.visualViewport.addEventListener("resize", (e) => {
      const pageHeight = e.target.height;
      setHeight(pageHeight + scrollY);
    });
    return;
  }, []);

  const updateImageSettings = async (values) => {
    setLoading(true);
    const formData = new FormData();
    let tagList = [];

    // seq info
    formData.append("seqPartner", info.seqPartner);
    formData.append("seqPartnerBranch", info.seqPartnerBranch);
    formData.append("headOfficeManagerYn", info.headOfficeManagerYn);

    // image
    if (values.profileImage.length) {
      if (!values.profileImage[0].id) {
        formData.append("changeYn", "Y");
        formData.append("profileImage", values?.profileImage[0]);
      } else {
        formData.append("changeYn", "N");
      }
    } else {
      if (info?.profileImage) formData.append("changeYn", "Y");
      else formData.append("changeYn", "N");
    }

    // memo
    formData.append("memo", values.memo);

    // tags
    if (values.tag1) tagList.push(values.tag1);
    if (values.tag2) tagList.push(values.tag2);
    if (values.tag3) tagList.push(values.tag3);
    formData.append("hashTagList", tagList);

    try {
      await api.patch("/partners/image", formData).then(({ data }) => {
        if (formData.get("changeYn") === "Y") dispatch(updatePartnerInfo());
        setDirty(false);
        navigate(-1);
        setLoading(false);
      });
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  const handleSetRef = useCallback(
    (node) => {
      if (node?.values) setFormikValues(node?.values);
    },
    [info]
  );

  useEffect(() => {
    if (
      deletedImages.length > 0 ||
      !compareObjects(formikValues, initialValues)
    ) {
      setDirty(true);
    } else {
      setDirty(false);
    }
  }, [formikValues, deletedImages]);

  usePrompt(
    "현재 작성중인 내용이 있어요.",
    `이대로 이동하면 입력한 내용이\n저장되지 않아요.\n그래도 페이지에서 나갈까요?`,
    dirty
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => updateImageSettings(values)}
      innerRef={(node) => handleSetRef(node)}
    >
      {() => (
        <Form>
          <Wrapper ref={pageRef} height={height}>
            <div>
              <ImagePicker
                name="profileImage"
                label="센터 대표 이미지"
                uploadLimit={1}
                setDeletedImages={setDeletedImages}
                setDirty={setDirty}
              />
              <TextArea
                name="memo"
                label="센터 설명"
                placeholder="센터에 대한 소개를 입력해주세요"
                maxLength={250}
              />
              <TextInput
                name="tag1"
                label="센터태그"
                placeholder="#태그입력"
                marginBottom="8px"
              />
              <TextInput
                name="tag2"
                placeholder="#태그입력"
                marginBottom="8px"
              />
              <TextInput name="tag3" placeholder="#태그입력" marginBottom="0" />
            </div>
            <ButtonWrapper>
              <Button
                type="submit"
                variant="contained"
                className="square"
                padding="16px"
                text="입력 완료"
              />
            </ButtonWrapper>
          </Wrapper>
        </Form>
      )}
    </Formik>
  );
};

const Wrapper = styled.div`
  padding: 16px 20px 42px 20px;
  background: #fff;
  height: ${(props) => `${props.height - 118}px`};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
`;

const ButtonWrapper = styled.div`
  display: flex;
  margin-top: 20px;
`;
export default ImageSettingsEditForm;
